import { useState, useEffect } from 'react';
import { 
  Briefcase, 
  Clock, 
  CheckCircle, 
  DollarSign, 
  Star, 
  TrendingUp,
  Users,
  Edit,
  Eye,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import type { Job, JobApplication, BroUser } from '../types';

interface MyJobsDashboardProps {
  user: BroUser;
  onBack: () => void;
}

type DashboardTab = 'overview' | 'posted' | 'applied' | 'active';

const MyJobsDashboard = ({ user }: MyJobsDashboardProps) => {
  const [activeTab, setActiveTab] = useState<DashboardTab>('overview');
  const [postedJobs, setPostedJobs] = useState<Job[]>([]);
  const [appliedJobs, setAppliedJobs] = useState<JobApplication[]>([]);
  const [activeJobs, setActiveJobs] = useState<Job[]>([]);
  const [stats, setStats] = useState({
    totalPosted: 0,
    totalApplications: 0,
    activeProjects: 0,
    totalEarned: 0,
    completionRate: 0,
    averageRating: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, [user.id]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch jobs posted by user
      const { data: userJobs } = await supabase
        .from('jobs')
        .select(`
          *,
          job_applications(count)
        `)
        .eq('employer_id', user.id)
        .order('created_at', { ascending: false });

      if (userJobs) {
        const jobsWithCounts = userJobs.map(job => ({
          ...job,
          applications_count: job.job_applications?.[0]?.count || 0,
          experience_level: job.difficulty_level || 'intermediate',
          priority: job.urgent ? 'urgent' : (job.featured ? 'high' : 'normal'),
          remote_allowed: job.location_type === 'remote',
          auto_assign: false
        })) as Job[];
        setPostedJobs(jobsWithCounts);
      }

      // Fetch applications by user with job details
      const { data: userApplications } = await supabase
        .from('job_applications')
        .select(`
          *,
          job:jobs(*)
        `)
        .eq('applicant_id', user.id)
        .order('applied_at', { ascending: false });

      if (userApplications) {
        const applicationsWithStatus = userApplications.map(app => ({
          ...app,
          status: app.status as 'pending' | 'reviewing' | 'shortlisted' | 'accepted' | 'rejected' | 'withdrawn',
          job: app.job ? {
            ...app.job,
            experience_level: app.job.difficulty_level || 'intermediate',
            priority: app.job.urgent ? 'urgent' : (app.job.featured ? 'high' : 'normal'),
            remote_allowed: app.job.location_type === 'remote',
            auto_assign: false
          } : undefined
        })) as JobApplication[];
        setAppliedJobs(applicationsWithStatus);
      }

      // Fetch active jobs assigned to user
      const { data: assignedJobs } = await supabase
        .from('jobs')
        .select('*')
        .eq('assigned_to', user.id)
        .in('status', ['assigned', 'in_progress'])
        .order('assigned_at', { ascending: false });

      if (assignedJobs) {
        const jobsWithDefaults = assignedJobs.map(job => ({
          ...job,
          experience_level: job.difficulty_level || 'intermediate',
          priority: job.urgent ? 'urgent' : (job.featured ? 'high' : 'normal'),
          remote_allowed: job.location_type === 'remote',
          auto_assign: false
        })) as Job[];
        setActiveJobs(jobsWithDefaults);
      }

      // Calculate total earnings from completed jobs
      const { data: completedJobs } = await supabase
        .from('jobs')
        .select('budget_min, budget_max, budget_currency')
        .eq('assigned_to', user.id)
        .eq('status', 'completed');

      let totalEarned = 0;
      completedJobs?.forEach(job => {
        if (job.budget_currency === 'BRO_SCORE') {
          totalEarned += (job.budget_max || job.budget_min || 0);
        }
      });

      // Calculate completion rate
      const { data: allAssignedJobs } = await supabase
        .from('jobs')
        .select('status')
        .eq('assigned_to', user.id)
        .in('status', ['completed', 'cancelled', 'disputed']);

      const completionRate = allAssignedJobs?.length 
        ? (allAssignedJobs.filter(job => job.status === 'completed').length / allAssignedJobs.length) * 100
        : 0;

      // Get average rating
      const { data: reviews } = await supabase
        .from('job_reviews')
        .select('rating')
        .eq('reviewee_id', user.id)
        .eq('is_public', true);

      const averageRating = reviews?.length 
        ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length
        : 0;

      setStats({
        totalPosted: userJobs?.length || 0,
        totalApplications: userApplications?.length || 0,
        activeProjects: assignedJobs?.length || 0,
        totalEarned,
        completionRate: Math.round(completionRate),
        averageRating: Math.round(averageRating * 10) / 10
      });

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-green-500';
      case 'assigned': return 'bg-blue-500';
      case 'in_progress': return 'bg-yellow-500';
      case 'completed': return 'bg-green-600';
      case 'pending': return 'bg-yellow-500';
      case 'accepted': return 'bg-green-500';
      case 'rejected': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    return `${diffInDays} days ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="animate-spin mr-2" size={20} />
        <span className="text-tan">Loading dashboard...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <div className="text-red-400 mb-2">{error}</div>
        <Button onClick={loadDashboardData} variant="outline" size="sm">
          Try Again
        </Button>
      </div>
    );
  }

  // Overview Tab
  if (activeTab === 'overview') {
    return (
      <div className="space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-navy-light rounded-lg p-4 border border-tan-dark">
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="text-tan" size={20} />
              <span className="text-tan font-bold text-lg">{stats.totalPosted}</span>
            </div>
            <p className="text-tan-dark text-sm">Jobs Posted</p>
          </div>

          <div className="bg-navy-light rounded-lg p-4 border border-tan-dark">
            <div className="flex items-center gap-2 mb-2">
              <Users className="text-tan" size={20} />
              <span className="text-tan font-bold text-lg">{stats.totalApplications}</span>
            </div>
            <p className="text-tan-dark text-sm">Applications</p>
          </div>

          <div className="bg-navy-light rounded-lg p-4 border border-tan-dark">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="text-tan" size={20} />
              <span className="text-tan font-bold text-lg">{stats.totalEarned}</span>
            </div>
            <p className="text-tan-dark text-sm">BroScore Earned</p>
          </div>

          <div className="bg-navy-light rounded-lg p-4 border border-tan-dark">
            <div className="flex items-center gap-2 mb-2">
              <Star className="text-tan" size={20} />
              <span className="text-tan font-bold text-lg">{stats.averageRating}</span>
            </div>
            <p className="text-tan-dark text-sm">Avg Rating</p>
          </div>
        </div>

        {/* Performance Card */}
        <div className="bg-navy-light rounded-lg p-4 border border-tan-dark">
          <h3 className="text-tan font-bold mb-3 flex items-center gap-2">
            <TrendingUp size={18} />
            Performance
          </h3>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-tan-dark">Completion Rate</span>
              <div className="flex items-center gap-2">
                <div className="w-24 bg-navy rounded-full h-2">
                  <div 
                    className="bg-tan h-2 rounded-full" 
                    style={{ width: `${stats.completionRate}%` }}
                  ></div>
                </div>
                <span className="text-tan font-medium">{stats.completionRate}%</span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-tan-dark">Response Time</span>
              <span className="text-tan font-medium">&lt; 2 hours</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-tan-dark">Repeat Clients</span>
              <span className="text-tan font-medium">3</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            onClick={() => setActiveTab('posted')}
            variant="outline"
            className="border-tan-dark text-tan hover:bg-tan hover:text-navy h-auto p-4"
          >
            <div className="text-center">
              <Briefcase size={24} className="mx-auto mb-2" />
              <div className="font-medium">Posted Jobs</div>
              <div className="text-xs opacity-75">{stats.totalPosted} active</div>
            </div>
          </Button>

          <Button
            onClick={() => setActiveTab('applied')}
            variant="outline"
            className="border-tan-dark text-tan hover:bg-tan hover:text-navy h-auto p-4"
          >
            <div className="text-center">
              <Clock size={24} className="mx-auto mb-2" />
              <div className="font-medium">Applications</div>
              <div className="text-xs opacity-75">{stats.totalApplications} submitted</div>
            </div>
          </Button>
        </div>

        {/* Recent Activity */}
        <div className="bg-navy-light rounded-lg p-4 border border-tan-dark">
          <h3 className="text-tan font-bold mb-3">Recent Activity</h3>
          
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-2 bg-navy rounded border border-tan-dark">
              <CheckCircle className="text-green-400" size={16} />
              <div className="flex-1">
                <p className="text-tan text-sm">Applied to Smart Contract Audit</p>
                <p className="text-tan-dark text-xs">2 hours ago</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-2 bg-navy rounded border border-tan-dark">
              <Briefcase className="text-blue-400" size={16} />
              <div className="flex-1">
                <p className="text-tan text-sm">Posted React Developer position</p>
                <p className="text-tan-dark text-xs">1 day ago</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-2 bg-navy rounded border border-tan-dark">
              <Users className="text-yellow-400" size={16} />
              <div className="flex-1">
                <p className="text-tan text-sm">5 new applications received</p>
                <p className="text-tan-dark text-xs">2 days ago</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Posted Jobs Tab
  if (activeTab === 'posted') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-tan">Jobs You've Posted</h3>
          <span className="text-tan-dark text-sm">{postedJobs.length} total</span>
        </div>

        {postedJobs.length === 0 ? (
          <div className="text-center py-8">
            <Briefcase className="mx-auto text-tan-dark mb-2" size={48} />
            <div className="text-tan-dark">No jobs posted yet</div>
            <div className="text-tan-dark text-sm">Post your first job to get started</div>
          </div>
        ) : (
          <div className="space-y-3">
            {postedJobs.map((job) => (
              <div key={job.id} className="bg-navy-light rounded-lg p-4 border border-tan-dark">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="text-tan font-bold">{job.title}</h4>
                    <div className="flex items-center gap-2 mt-1 text-sm text-tan-dark">
                      <Badge className={`${getStatusColor(job.status)} text-white text-xs`}>
                        {job.status.toUpperCase()}
                      </Badge>
                      <span>•</span>
                      <span>{formatTimeAgo(job.created_at)}</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="border-tan-dark text-tan hover:bg-tan hover:text-navy">
                      <Eye size={14} className="mr-1" />
                      View
                    </Button>
                    <Button size="sm" variant="outline" className="border-tan-dark text-tan hover:bg-tan hover:text-navy">
                      <Edit size={14} />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-tan-dark">Budget:</span>
                    <p className="text-tan font-medium">
                      {job.budget_min}-{job.budget_max} {job.budget_currency}
                    </p>
                  </div>
                  <div>
                    <span className="text-tan-dark">Applications:</span>
                    <p className="text-tan font-medium">{job.applications_count || 0}</p>
                  </div>
                  <div>
                    <span className="text-tan-dark">Experience:</span>
                    <p className="text-tan font-medium capitalize">{job.experience_level}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Applied Jobs Tab
  if (activeTab === 'applied') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-tan">Your Applications</h3>
          <span className="text-tan-dark text-sm">{appliedJobs.length} total</span>
        </div>

        {appliedJobs.length === 0 ? (
          <div className="text-center py-8">
            <Users className="mx-auto text-tan-dark mb-2" size={48} />
            <div className="text-tan-dark">No applications yet</div>
            <div className="text-tan-dark text-sm">Browse jobs and apply to get started</div>
          </div>
        ) : (
          <div className="space-y-3">
            {appliedJobs.map((application) => (
              <div key={application.id} className="bg-navy-light rounded-lg p-4 border border-tan-dark">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="text-tan font-bold">{application.job?.title}</h4>
                    <div className="flex items-center gap-2 mt-1 text-sm text-tan-dark">
                      <Badge className={`${getStatusColor(application.status)} text-white text-xs`}>
                        {application.status.toUpperCase()}
                      </Badge>
                      <span>•</span>
                      <span>Applied {formatTimeAgo(application.applied_at)}</span>
                    </div>
                  </div>
                  
                  <Button size="sm" variant="outline" className="border-tan-dark text-tan hover:bg-tan hover:text-navy">
                    <Eye size={14} className="mr-1" />
                    View
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-tan-dark">Your Proposal:</span>
                    <p className="text-tan font-medium">
                      {application.proposed_budget} {application.job?.budget_currency}
                    </p>
                  </div>
                  <div>
                    <span className="text-tan-dark">Timeline:</span>
                    <p className="text-tan font-medium">{application.proposed_timeline}</p>
                  </div>
                </div>

                <div className="mt-3 p-2 bg-navy rounded border border-tan-dark">
                  <p className="text-tan-dark text-xs line-clamp-2">
                    {application.cover_letter}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Active Jobs Tab
  if (activeTab === 'active') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-tan">Active Projects</h3>
          <span className="text-tan-dark text-sm">{activeJobs.length} active</span>
        </div>

        <div className="text-center py-8">
          <Clock className="mx-auto text-tan-dark mb-2" size={48} />
          <div className="text-tan-dark">No active projects</div>
          <div className="text-tan-dark text-sm">Your accepted projects will appear here</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-navy-light rounded-lg p-1 border border-tan-dark">
        {[
          { key: 'overview', label: 'Overview', icon: TrendingUp },
          { key: 'posted', label: 'Posted', icon: Briefcase },
          { key: 'applied', label: 'Applied', icon: Users },
          { key: 'active', label: 'Active', icon: Clock }
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key as DashboardTab)}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1 ${
              activeTab === key
                ? 'bg-tan text-navy'
                : 'text-tan-dark hover:text-tan'
            }`}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content is rendered above based on activeTab */}
    </div>
  );
};

export default MyJobsDashboard;